import React, { FunctionComponent, useEffect, useId, useRef } from 'react';

type InputUIProps = {
  label_text: string,
  value: string | number | undefined,
  onTextChange: Function,
  help_text: string,
  type: 'text' | 'number' | 'password',
  width: number,
  multiline: boolean,
  icon?: JSX.Element,
  iconPlacement?: 'start' | 'end',
  error?: boolean,
  onKeyDown?: Function,
  disabled?: boolean,
  textAlign?: 'center' | 'left' | 'right',
  required?: boolean,
  autoFocus?: boolean,
};

/**
 * Text field.
 *
 * Public API is unchanged, but the appearance is now the design system's
 * `.ds-field` — a bordered box with the label above it — instead of the MUI
 * floating-label-over-underline. That makes it match the rest of the system and
 * lets it follow the active theme.
 *
 * Two fixes carried in while converting:
 *
 *  - **Unique ids.** This component used to hardcode `id="textfield"` and
 *    `aria-describedby="component-helper-text"`, so all ~133 instances in the
 *    app emitted the same DOM ids — clicking any label focused the first input
 *    on the page. Both are now generated per instance with `useId`.
 *  - **`multiline` actually works.** The prop was declared and accepted but
 *    never rendered a textarea. It now renders a textarea that starts at one
 *    row and auto-grows as the content wraps — same visual footprint as the
 *    single-line input until the user actually needs more space.
 *
 * `width` stays a percentage, as call sites expect (`width={50}` → 50%).
 */
const InputUI: FunctionComponent<InputUIProps> = ({
  label_text,
  required = false,
  value,
  onTextChange,
  help_text,
  type,
  width,
  multiline,
  icon,
  iconPlacement = 'start',
  error = false,
  onKeyDown,
  disabled = false,
  textAlign = 'left',
  autoFocus = false,
}) => {
  const autoId = useId();
  const fieldId = `input-${autoId}`;
  const hintId = `${fieldId}-hint`;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-size the textarea to its content: reset to `auto` first so it can
  // shrink when text is deleted, then match its scrollHeight. Runs on mount so
  // an initial `value` is sized correctly, and on every value change while
  // `multiline` is on.
  useEffect(() => {
    if (!multiline) return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, multiline]);

  const sharedProps = {
    id: fieldId,
    value: value ?? '',
    disabled,
    autoFocus,
    required,
    'aria-invalid': error || undefined,
    'aria-describedby': help_text ? hintId : undefined,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onTextChange(e.target.value),
    onKeyDown: (e: React.KeyboardEvent) => { if (onKeyDown !== undefined) onKeyDown(e); },
    className: 'ds-field',
    'data-ds-invalid': error ? 'true' : undefined,
    style: { textAlign },
  };

  return (
    <div style={{ width: width + '%' }} className="ds-control-root inline-flex flex-col align-top">
      <label className="ds-label" htmlFor={fieldId}>
        {label_text}
        {required && <span className="ds-required" aria-hidden="true">*</span>}
      </label>

      {/* The icon sits inside the box, so the control keeps one border. */}
      <div className={icon ? 'ds-search' : undefined}>
        {icon && iconPlacement === 'start' && (
          <span className="ds-search-icon">{icon}</span>
        )}
        {multiline
          ? <textarea
              ref={textareaRef}
              rows={1}
              {...sharedProps}
              // `textarea.ds-field` has `min-height: 64px` so it never renders
              // shorter than ~2 rows. Override to the single-line control
              // height so auto-grow can start from one row.
              style={{ ...sharedProps.style, resize: 'none', overflow: 'hidden', minHeight: 'var(--ds-control-h)' }}
            />
          : <input type={type} {...sharedProps} />}
        {/* End-placed icons are usually interactive (clear, search), so they get
            `.ds-field-action` — vertically centred and clickable — rather than
            `.ds-search-icon`, which is decorative and `pointer-events: none`. */}
        {icon && iconPlacement === 'end' && (
          <span className="ds-field-action">{icon}</span>
        )}
      </div>

      {help_text && (
        <span className="ds-hint" id={hintId} data-ds-invalid={error ? 'true' : undefined}>
          {help_text}
        </span>
      )}
    </div>
  );
};

export default InputUI;
